import { Theme } from '@emotion/react';
import {
  FormControl,
  Grid,
  SxProps,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { Controller, FieldError } from 'react-hook-form';

import { gridSize } from '@/shared/constants';

export enum TiposTextFieldNumberEnum {
  CELULAR = 'CELULAR',
  TELEFONO = 'TELEFONO',
  TARJETA = 'TARJETA',
  IDENTIFICACION = 'IDENTIFICACION',
  DEFECTO = 'DEFECTO',
}


type CustomTextFieldProps = {
  label: string;
  error: FieldError | undefined;
  //helperText: React.ReactNode;
  helperText?: string
  disabled?: boolean;
  shrink?: boolean;
  required?: boolean;
  readonly?: boolean;

  type?: React.HTMLInputTypeAttribute;

  size?: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
  };

  register?: TextFieldProps;

  control?: any;
  name?: string;
  defaultValue?: string | number | null;
  onChangeValue?: (value: any) => void;

  sizeTextField?: 'medium' | 'small';
  startAdornment?: React.ReactNode;

  sxTextField?: SxProps<Theme>;

  placeholder?: string;

  overrideAsPassword?: boolean;
  endAdornmentInput?: React.ReactNode;

  max?: number;
  customRestriccionCelular?: {
    tolerancia_repeticion: number,
    no_permitidos: string[],
    longitud_subgrupos: number,
  };
  soloAdvertenciaRestriccion?: boolean;
};

const CustomTextFieldNumberComponente: React.FC<CustomTextFieldProps> = ({
  label,
  error,
  helperText,
  required = true,
  disabled = false,
  shrink = false,
  size = gridSize,
  type = 'text',
  control,
  name,
  defaultValue,
  onChangeValue,
  sizeTextField = 'medium',

  startAdornment,
  sxTextField,

  placeholder,
  endAdornmentInput,

  max = 10,
  customRestriccionCelular,
  soloAdvertenciaRestriccion = false,
}) => {
  
  const [errorAux, setErrorAux] = useState<boolean>(!!error);
  const [helperTextAux, setHelperTextAux] = useState(helperText);
  const [isAdvertenciaRestriccion, setIsAdvertenciaRestriccion] = useState<boolean>(false);
  const [isModified, setIsModified] = useState<boolean>(false);

  useEffect(() => {
    if (error) {
      setIsModified(false);
      setErrorAux(true);
      setHelperTextAux(error.message || helperText);
    } else {
      setErrorAux(false);
      setHelperTextAux(helperText || '');
    }
  }, [error, helperText]);

  const validarRestriccionHelper = (valor: string): string => {
    if (!customRestriccionCelular) return '';

    const tolerancia = customRestriccionCelular.tolerancia_repeticion ?? 0;
    const longitudSubgrupos = customRestriccionCelular.longitud_subgrupos ?? 0;

    // 1. No permitir más de N caracteres iguales consecutivos
    if (tolerancia > 0) {
      const regexToleranciaRepeticion = new RegExp(
        String.raw`(\d)\1{${tolerancia}}`
      );
      if (regexToleranciaRepeticion.test(valor)) {
        return `El número ingresado no es valido. No se permiten más de ${tolerancia} números repetidos consecutivamente. Verifica que sea correcto para asegurar el contacto en postventa. Puedes continuar con el proceso de preventa.`;
      }
    }

    // 2. No permitir grupos repetidos consecutivamente
    if (longitudSubgrupos > 0) {
      const regexLongitudSubgrupos = new RegExp(
        String.raw`(\d{${longitudSubgrupos},})\1+`
      );
      if (regexLongitudSubgrupos.test(valor)) {
        return 'El número ingresado no es valido. No se permiten secuencias repetidas de números. Verifica que sea correcto para asegurar el contacto en postventa. Puedes continuar con el proceso de preventa.';
      }
    }

    // 3. No permitir secuencias parametrizadas
    if (
      customRestriccionCelular.no_permitidos?.some(
        sequence => valor.includes(sequence)
      )
    ) {
      return 'El número ingresado no es valido. Contiene una secuencia de números no permitida. Verifica que sea correcto para asegurar el contacto en postventa. Puedes continuar con el proceso de preventa.';
    }

    return '';
  };

  return (
    <Grid item {...size}>

      <FormControl fullWidth variant="outlined">
        <Controller

          control={control}
          name={name!}
          defaultValue={defaultValue ?? ''}
          rules={{
            validate: () => {
              if (errorAux && !isAdvertenciaRestriccion) {
                return helperTextAux || 'El valor ingresado no es válido';
              }

              return true;
            },
          }}
          render={({ field }) => {
            const onChange = (event: any) => {
              setIsModified(true);
              let inputValue = event.target.value;

              if (inputValue == '' || !inputValue) {
                field.onChange('');
                onChangeValue && onChangeValue('');
                setHelperTextAux(undefined);
                setErrorAux(false);
                setIsAdvertenciaRestriccion(false);
                return;
              }

              if (!/^\d+$/.test(inputValue)) return;

              switch (type) {
                case TiposTextFieldNumberEnum.CELULAR:
                  if (inputValue.length >= 2 && !inputValue.startsWith('09')) {
                    inputValue = inputValue.slice(0, 2);
                    setHelperTextAux(`Los dos primeros caracteres deben empezar con "09"`);
                    setErrorAux(true);
                    setIsAdvertenciaRestriccion(false);
                  } else if (inputValue.length < max) {
                    setHelperTextAux('El número debe contener 10 caracteres');
                    setErrorAux(true);
                    setIsAdvertenciaRestriccion(false);
                  } else {
                    inputValue = inputValue.slice(0, max);
                    setHelperTextAux(``);
                    setErrorAux(false);
                    setIsAdvertenciaRestriccion(false);
                  }

                  if (customRestriccionCelular && inputValue.length === max && inputValue.startsWith('09')) {
                    const mensajeRestriccion = validarRestriccionHelper(inputValue);
                    if (mensajeRestriccion) {
                      setHelperTextAux(mensajeRestriccion);
                      if (soloAdvertenciaRestriccion) {
                        setIsAdvertenciaRestriccion(true);
                      } else {
                        setErrorAux(true);
                        setIsAdvertenciaRestriccion(false);
                      }
                    } else if (soloAdvertenciaRestriccion) {
                      setIsAdvertenciaRestriccion(false);
                    }
                  }

                  break;
                case TiposTextFieldNumberEnum.TELEFONO: {
                    const validatePrefix = /^(02|03|04|05|06|07)/;
                    if (inputValue.length < 2) {
                      setHelperTextAux(``);
                      setErrorAux(false);
                      setIsAdvertenciaRestriccion(false);

                    // A partir de 2 caracteres, validar únicamente el prefijo
                    } else if (!validatePrefix.test(inputValue)) {
                      setHelperTextAux(
                        `Debe ingresar un prefijo telefónico válido, por ejemplo: 02 (Pichincha, Santo Domingo de los Tsáchilas), 04 (Guayas, Santa Elena), etc.`
                      );
                      setErrorAux(true);
                      setIsAdvertenciaRestriccion(false);

                    } else {
                      setHelperTextAux(``);
                      setErrorAux(false);
                      setIsAdvertenciaRestriccion(false);
                    }


                    if (customRestriccionCelular) {
                      const mensajeRestriccion = validarRestriccionHelper(inputValue);
                      if (mensajeRestriccion) {
                        setHelperTextAux(mensajeRestriccion);
                        if (soloAdvertenciaRestriccion) {
                          setIsAdvertenciaRestriccion(true);
                        } else {
                          setErrorAux(true);
                          setIsAdvertenciaRestriccion(false);
                        }
                      } else if (soloAdvertenciaRestriccion) {
                        setIsAdvertenciaRestriccion(false);
                      }
                    }
                    break;
                }
                case TiposTextFieldNumberEnum.TARJETA:
                  if (inputValue.length >= max) {
                    inputValue = inputValue.slice(0, max);
                    setErrorAux(false);
                  }
                  break;

                case TiposTextFieldNumberEnum.IDENTIFICACION:
                  if (inputValue.length >= 10) {
                    inputValue = inputValue.slice(0, max);
                    setErrorAux(false);
                  }
                  break;

                default:
                  inputValue = inputValue.slice(0, max);
                  break;
              }

              field.onChange(inputValue);
              onChangeValue && onChangeValue(inputValue);
            };


            const hasError = isModified
              ? (isAdvertenciaRestriccion ? false : errorAux)
              : (!!error || (errorAux && !isAdvertenciaRestriccion));

            const displayedHelperText = isModified
              ? (errorAux || isAdvertenciaRestriccion ? (helperTextAux || '') : '')
              : (error?.message || (errorAux || isAdvertenciaRestriccion ? helperTextAux : helperText) || '');

            return (
              <TextField
                {...field}
                autoComplete='off'
                autoCorrect='off'
                size={sizeTextField}
                fullWidth
                variant="outlined"
                label={label}
                InputLabelProps={{
                  ...(shrink && { shrink: true }),
                  ...(startAdornment && { startAdornment }),
                }}
                InputProps={{
                  ...(endAdornmentInput && {
                    endAdornment: endAdornmentInput,
                    size: 'medium'
                  }),
                  startAdornment: startAdornment ?? null,
                }}
                error={hasError}
                placeholder={placeholder ?? ''}
                helperText={displayedHelperText}
                FormHelperTextProps={{
                  sx: isAdvertenciaRestriccion && !hasError ? {
                    color: 'warning.main',
                    fontWeight: 500,
                  } : undefined,
                }}
                type={type}
                onChange={onChange}
                required={required}
                inputProps={{ readOnly: disabled, maxLength: max }}
                sx={{
                  ...sxTextField,
                  ...(disabled && { background: 'rgba(0, 0, 0, 0.04)' }),
                }}

              />
            );
          }}
        ></Controller>
      </FormControl>

    </Grid>
  );
};

export default CustomTextFieldNumberComponente;